<script setup>
import { StatusIndicator } from 'vue-status-indicator'
import { dateSince, latLngToLocator } from '../utils/stationUtils'
import { computed } from 'vue'

const props = defineProps({
  station: {
    type: Object,
    required: true
  },
  isCurrentUser: {
    type: Boolean,
    required: true
  }
})

const confirmedPictures = computed(() => {
  if (!props.station || !props.station.pictures) return null
  return props.station.pictures.filter(pic => pic.status == 1)
})
</script>

<template>
  <v-card flat class="mr-5 my-3 pa-2 grey--text text--darken-3">
    <v-carousel v-if="confirmedPictures && confirmedPictures.length > 0">
      <v-carousel-item
        v-for="(picture,i) in confirmedPictures"
        :key="i"
        :src="picture.src"
        reverse-transition="fade-transition"
        transition="fade-transition"
      ></v-carousel-item>
    </v-carousel>
    <v-card-text class="mt-3 grey--text text--darken-3 mx-auto text-center">
      <v-layout row wrap>
        <v-flex pa-2 xs6 sm6 md6 lg3 xl3>
          <div class="caption grey--text">Status</div>
          <status-indicator class="mr-1" :status="props.station.status==1?'positive':'negative'" :pulse="props.station.status==1" /> 
          <strong v-html="(props.station.status==1) ? '<span class=\'green--text\'>Online</span>' : '<span class=\'red--text\'>Offline</span>'"></strong>
        </v-flex>
        <v-flex pa-2 xs6 sm6 md6 lg3 xl3>
          <div class="caption grey--text">Listening</div>
          <div>{{props.station.satellite}}</div>
        </v-flex>
        <v-flex pa-2 xs6 sm6 md6 lg3 xl3>
          <div class="caption grey--text">Version</div>
          <div>{{props.station.version}}</div>
        </v-flex>
        <v-flex pa-2 xs6 sm6 md6 lg3 xl3>
          <div class="caption grey--text">Creation date</div>
          <div>{{dateSince(props.station.creationDate)}}</div>
        </v-flex>
        <v-flex pa-2 xs6 sm6 md6 lg3 xl3>
          <div class="caption grey--text">Last seen</div>
          <div>{{dateSince(props.station.lastSeen)}}</div>
        </v-flex>
        <v-flex pa-2 xs6 sm6 md6 lg3 xl3>
          <div class="caption grey--text">Last Packet</div>
          <div>{{dateSince(props.station.lastPacketTime)}}</div>
        </v-flex>
        <v-flex pa-2 xs6 sm6 md6 lg3 xl3>
          <div class="caption grey--text">Position (Lat, Long)</div>
          <div>{{props.station.location[0]}}, {{props.station.location[1]}}</div>
        </v-flex>
        <v-flex pa-2 xs6 sm6 md6 lg3 xl3>
          <div class="caption grey--text">QTH Locator</div>
          <div>{{latLngToLocator(props.station.location[0], props.station.location[1])}}</div>
        </v-flex>
        <v-flex pa-2 xs6 sm6 md6 lg3 xl3>
          <div class="caption grey--text">Elevation</div>
          <div>{{(props.station.elevation||0).toFixed(2)}} m</div>
        </v-flex>
        <v-flex pa-2 xs6 sm6 md6 lg3 xl3>
          <div class="caption grey--text">Auto tuning</div>
          <div>{{props.station.autoTune?props.station.autoTune:"OFF"}}</div>
        </v-flex>
        <v-flex pa-2 xs6 sm6 md6 lg3 xl3>
          <div class="caption grey--text">Test mode</div>
          <div>{{props.station.test?"ON":"OFF"}}</div>
        </v-flex>
        <v-flex pa-2 xs6 sm6 md6 lg3 xl3>
          <div class="caption grey--text">Confirmed packets</div>
          <div>{{props.station.confirmedPackets }}</div>
        </v-flex>
        <v-flex pa-2 xs6 sm6 md6 lg3 xl3>
          <div class="caption grey--text">Telemetry packets</div>
          <div>{{props.station.telemetryPackets}}</div>
        </v-flex>
        <v-flex pa-2 xs6 sm6 md6 lg3 xl3>
          <div class="caption grey--text">Type of antenna</div>
          <div>{{props.station.antenna ? props.station.antenna : "- -"}}</div>
        </v-flex>
        <v-flex pa-2 xs6 sm6 md6 lg3 xl3>
          <div class="caption grey--text">Band</div>
          <div>{{props.station.antennaBand ? `${props.station.antennaBand[0]} - ${props.station.antennaBand[1]} MHz` : "- -"}}</div>
        </v-flex>
        <v-flex pa-2 xs6 sm6 md6 lg3 xl3>
          <div class="caption grey--text">Record distance</div>
          <div>{{ props.station.recordDistance ? (props.station.recordDistance||0).toFixed(1) + " Km" : "- -"}}</div>
        </v-flex>
        <v-flex pa-2 xs6 sm6 md6 lg3 xl3 v-if="props.isCurrentUser">
          <div class="caption grey--text">Local IP</div>
          <div><a target="blank" :href="`http://${props.station.local_ip}/`">{{props.station.local_ip}}</a></div>
        </v-flex>
        <v-flex pa-2 xs6 sm6 md6 lg3 xl3 v-if="props.isCurrentUser">
          <div class="caption grey--text">Auto update</div>
          <div>{{props.station.autoUpdate?"ON":"OFF"}}</div>
        </v-flex>
        <v-flex pa-2 xs6 sm6 md6 lg3 xl3 v-if="props.isCurrentUser">
          <div class="caption grey--text">Wifi RSSI</div>
          <div :class="{ 'green--text font-weight-bold': props.station.wifiRssi >= -70, ' red--text ': props.station.wifiRssi < -80 }">
            {{
              props.station.wifiRssi ? (
                props.station.wifiRssi >= -60 ? "Very Good" : (
                  props.station.wifiRssi >= -70 ? "Good" : (
                    props.station.wifiRssi >= -80 ? "Low" : "Very low"
                  )
                )
              ) : "None"
            }}
          </div>
        </v-flex>
        <v-flex pa-2 xs6 sm6 md6 lg3 xl3 v-if="props.isCurrentUser">
          <div class="caption grey--text">Radio Status</div>
          <div :class="{ 'green--text font-weight-bold': props.station.radioStatus === 0, 'red--text font-weight-bold': props.station.radioStatus !== 0 }">
            {{
              props.station.radioStatus === undefined ? "None" : (
                props.station.radioStatus === 0 ? "Ready" : `Error: ${props.station.radioStatus}`
              )
            }}
          </div>
        </v-flex>
        <v-flex pa-2 xl12 v-if="props.station.description">
          <div class="caption grey--text">Description</div>
          <div class="px-8">{{props.station.description}}</div>
        </v-flex>
      </v-layout>
    </v-card-text>
  </v-card>
</template>